export interface TaskAttachment {
  id: string;
  task_id: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  display_name: string | null | undefined;
  alt_text: string | null | undefined;
  is_primary: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface CreateAttachmentRequest {
  task_id: string;
  file: File;
  display_name?: string;
  alt_text?: string;
  is_primary?: boolean;
}

export interface UpdateAttachmentRequest {
  display_name?: string;
  alt_text?: string;
  is_primary?: boolean;
}

export interface AttachmentUploadProgress {
  progress: number;
  isUploading: boolean;
  error?: string;
}

// Enhanced search interface for attachments
export interface AttachmentSearchOptions {
  query?: string;
  fileTypes?: AttachmentFileCategory[];
  sizeRange?: {
    min?: number; // bytes
    max?: number; // bytes
  };
  dateRange?: {
    from?: Date;
    to?: Date;
  };
  taskIds?: string[];
  userIds?: string[];
  isPrimary?: boolean;
  sortBy?: AttachmentSortField;
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface AttachmentSearchResult {
  attachments: TaskAttachment[];
  total: number;
  hasMore: boolean;
  aggregations?: {
    totalSize: number;
    countByType: Record<AttachmentFileCategory, number>;
    countByUser: Record<string, number>;
  };
}

export type AttachmentFileCategory =
  | "image"
  | "document"
  | "video"
  | "audio"
  | "other";

export type AttachmentSortField =
  | "created_at"
  | "file_size"
  | "file_name"
  | "display_name"
  | "file_type";

// Storage naming conventions
export interface AttachmentStorageConfig {
  bucketName: string;
  getFilePath: (userId: string, taskId: string, fileName: string) => string;
  getArchivePath: (userId: string, taskId: string, fileName: string) => string;
  getThumbnailPath: (
    userId: string,
    taskId: string,
    fileName: string
  ) => string;
}

// Default storage configuration following proper naming conventions
// Paths must start with auth.uid() — storage RLS uses storage.foldername(name)[1].
export const DEFAULT_ATTACHMENT_STORAGE: AttachmentStorageConfig = {
  bucketName: "task-attachments",
  getFilePath: (userId: string, taskId: string, fileName: string) =>
    `${userId}/tasks/${taskId}/attachments/${fileName}`,
  getArchivePath: (userId: string, taskId: string, fileName: string) =>
    `${userId}/tasks/${taskId}/archive/${fileName}`,
  getThumbnailPath: (userId: string, taskId: string, fileName: string) =>
    `${userId}/tasks/${taskId}/thumbnails/${fileName}`,
};

// Supported file types for validation - Expanded to match task metadata indicators
export const SUPPORTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/bmp",
  "image/tiff",
  "image/tif",
] as const;

export const SUPPORTED_DOCUMENT_TYPES = [
  "application/pdf",
  "text/plain",
  "text/markdown",
  "text/csv",
  "text/html",
  "application/json",
  // Microsoft Office
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
] as const;

export const SUPPORTED_VIDEO_TYPES = [
  "video/mp4",
  "video/avi",
  "video/quicktime", // .mov
  "video/x-ms-wmv",
  "video/x-flv",
  "video/webm",
  "video/x-matroska", // .mkv
  "video/x-m4v",
  "video/3gpp",
] as const;

export const SUPPORTED_AUDIO_TYPES = [
  "audio/mpeg", // .mp3
  "audio/wav",
  "audio/ogg",
  "audio/aac",
  "audio/flac",
  "audio/x-m4a",
  "audio/x-ms-wma",
] as const;

export const SUPPORTED_FILE_TYPES = [
  ...SUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_DOCUMENT_TYPES,
  ...SUPPORTED_VIDEO_TYPES,
  ...SUPPORTED_AUDIO_TYPES,
] as const;

// Maximum file size (100MB - increased for video/audio files)
export const MAX_FILE_SIZE = 100 * 1024 * 1024;

// File type helpers
export const isImageFile = (mimeType: string): boolean => {
  return (SUPPORTED_IMAGE_TYPES as readonly string[]).includes(mimeType);
};

export const isDocumentFile = (mimeType: string): boolean => {
  return (SUPPORTED_DOCUMENT_TYPES as readonly string[]).includes(mimeType);
};

export const isVideoFile = (mimeType: string): boolean => {
  return (SUPPORTED_VIDEO_TYPES as readonly string[]).includes(mimeType);
};

export const isAudioFile = (mimeType: string): boolean => {
  return (SUPPORTED_AUDIO_TYPES as readonly string[]).includes(mimeType);
};

export const isSupportedFileType = (mimeType: string): boolean => {
  return (SUPPORTED_FILE_TYPES as readonly string[]).includes(mimeType);
};

// Get file category for display purposes
export const getFileCategory = (mimeType: string): AttachmentFileCategory => {
  if (isImageFile(mimeType)) return "image";
  if (isDocumentFile(mimeType)) return "document";
  if (isVideoFile(mimeType)) return "video";
  if (isAudioFile(mimeType)) return "audio";
  return "other";
};

// File size formatter
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Enhanced file name utilities for proper naming conventions
export const generateAttachmentFileName = (originalName: string): string => {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split(".").pop() || "";
  const baseName = originalName
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-zA-Z0-9]/g, "-")
    .toLowerCase()
    .substring(0, 50);

  return `${timestamp}-${randomId}-${baseName}.${extension}`;
};

export const sanitizeFileName = (fileName: string): string => {
  return fileName
    .replace(/[^a-zA-Z0-9.-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
};
